import * as React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import useSWR from 'swr'
import {
  addLandlord,
  LANDLORD_PAGE_SIZE,
  LandlordRequestError,
  listLandlords,
  removeLandlord,
  resendLandlordSetPasswordLink,
  type LandlordPage,
} from '@/lib/bff/landlords'

const swrOptions = { revalidateOnFocus: false, shouldRetryOnError: false } as const

function useToken() {
  const { getAccessTokenSilently } = useAuth0()
  return React.useCallback(async () => {
    const token = await getAccessTokenSilently()
    if (!token) throw new LandlordRequestError(401, null, 'No access token available')
    return token
  }, [getAccessTokenSilently])
}

/* One page of Landlords, keyed by page and size. Pass `enabled: false` to make no request. */
export function useLandlords(page: number, enabled: boolean) {
  const token = useToken()
  const { data, error, isLoading, mutate } = useSWR<LandlordPage>(
    enabled ? ['bff/landlords', page, LANDLORD_PAGE_SIZE] : null,
    async ([, p, size]: [string, number, number]) => listLandlords(await token(), p, size),
    swrOptions,
  )
  return { page: data, error, isLoading, revalidate: () => mutate() }
}

/* The three Landlord mutations. Callers revalidate the list afterwards. */
export function useLandlordMutations() {
  const token = useToken()
  return React.useMemo(
    () => ({
      add: async (email: string) => addLandlord(await token(), email),
      remove: async (userId: string) => removeLandlord(await token(), userId),
      resendLink: async (userId: string) => resendLandlordSetPasswordLink(await token(), userId),
    }),
    [token],
  )
}
